import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { hourlyFogForecastService } from '../../services/HourlyFogForecastService';
import { metarFogForecastService } from '../../services/MetarFogForecastService';
import { mapHourlyForecastToTimelineData, getAirportName } from '../../utils/HourlyForecastTimelineMapper';
import { mapForecastToHourlyTimelineData } from '../../utils/SharedForecastTimelineMapper';
import {
  HourlyForecastTimelineData,
  HourlyForecastTimelineSegment,
} from '../../models/HourlyForecastTimelineData';

export interface VerificationPredictionRow {
  label: string;
  hoursAgo: number;
  requestedTime: Date;
  segments: HourlyForecastTimelineSegment[];
}

export interface VerificationData {
  locationId: number;
  locationName: string;
  referenceTime: Date;
  actuals: HourlyForecastTimelineData | null;
  predictions: VerificationPredictionRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date;
}

/**
 * For each actuals hour-slot T and lead time N (1-4h), fetches the prediction
 * made N hours before T. The API at requested_time=R returns 4 hourly buckets
 * at R, R+1h, R+2h, R+3h. To get bucket_start=T at lead N: R = T - (N-1)*1h.
 */
export function useVerificationData(
  locationId: number | null,
  isMetarLocation: boolean,
  customNow?: Date | null
): VerificationData {
  const [actuals, setActuals] = useState<HourlyForecastTimelineData | null>(null);
  const [predictions, setPredictions] = useState<VerificationPredictionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [locationName, setLocationName] = useState('');
  const isMounted = useRef(true);

  const referenceTime = useMemo(() => customNow || new Date(), [customNow]);

  const fetchData = useCallback(async () => {
    if (!locationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const name = getAirportName(locationId);
    setLocationName(name);
    const ref = customNow || new Date();

    const centerPoint = new Date(ref);
    const mins = centerPoint.getUTCMinutes();
    centerPoint.setUTCMinutes(Math.floor(mins / 15) * 15, 0, 0);

    const hourSlots = [4, 3, 2, 1].map(h =>
      new Date(centerPoint.getTime() - h * 60 * 60 * 1000)
    );

    // Collect unique call times: for each slot T and lead N, call at T - (N-1)*1h
    const callTimeSet = new Set<number>();
    callTimeSet.add(ref.getTime());
    for (const slot of hourSlots) {
      for (let lead = 1; lead <= 4; lead++) {
        callTimeSet.add(slot.getTime() - (lead - 1) * 60 * 60 * 1000);
      }
    }
    const callTimes = Array.from(callTimeSet).sort((a, b) => a - b).map(ms => new Date(ms));

    try {
      const results = await Promise.allSettled(
        callTimes.map(reqTime => {
          if (isMetarLocation) {
            return metarFogForecastService.getBatchMetarFogForecast([locationId], reqTime)
              .then(result => {
                const forecast = result.forecasts[0];
                if (!forecast) throw new Error('No METAR forecast returned');
                return mapForecastToHourlyTimelineData(forecast, locationId, name);
              });
          } else {
            return hourlyFogForecastService.getBatchHourlyFogForecast([locationId], reqTime)
              .then(result => {
                const forecast = result.forecasts[0];
                if (!forecast) throw new Error('No hourly forecast returned');
                return mapHourlyForecastToTimelineData(forecast, locationId, name);
              });
          }
        })
      );

      if (!isMounted.current) return;

      const nowIdx = callTimes.findIndex(t => t.getTime() === ref.getTime());
      const nowResult = results[nowIdx];
      if (!nowResult || nowResult.status !== 'fulfilled') {
        setError('Failed to load actuals data');
        setLoading(false);
        return;
      }

      const actualsSegments = nowResult.value.segments.filter(s => s.raw_actuals !== null);
      setActuals({
        ...nowResult.value,
        segments: actualsSegments
      });

      const dataByCallTime = new Map<number, HourlyForecastTimelineData>();
      callTimes.forEach((ct, idx) => {
        const r = results[idx];
        if (r.status === 'fulfilled') {
          dataByCallTime.set(ct.getTime(), r.value);
        }
      });

      const predictionRows: VerificationPredictionRow[] = [];

      for (let lead = 1; lead <= 4; lead++) {
        const rowSegments: HourlyForecastTimelineSegment[] = [];

        for (const slot of hourSlots) {
          const callTimeMs = slot.getTime() - (lead - 1) * 60 * 60 * 1000;
          const mapped = dataByCallTime.get(callTimeMs);

          if (mapped) {
            const slotHour = slot.getUTCHours();
            const predSeg = mapped.segments.find(s =>
              s.raw_predictions !== null && s.time.getUTCHours() === slotHour
            );
            if (predSeg) {
              rowSegments.push(predSeg);
            }
          }
        }

        predictionRows.push({
          label: `${lead}h ago`,
          hoursAgo: lead,
          requestedTime: new Date(centerPoint.getTime() - lead * 60 * 60 * 1000),
          segments: rowSegments
        });
      }

      setPredictions(predictionRows);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching verification data:', err);
      if (!isMounted.current) return;
      setError('Failed to load verification data');
      setLoading(false);
    }
  }, [locationId, isMetarLocation, customNow]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    locationId: locationId || 0,
    locationName,
    referenceTime,
    actuals,
    predictions,
    loading,
    error,
    refresh: fetchData,
    lastUpdated
  };
}
